/**
 * Prompt Templates for Universal LLM Question Generator
 * Supports multiple subject areas: MINT, Languages, Humanities, Arts
 */

import { Logger } from '../logger';

const log = Logger.create('promptTemplates');

// ============================================================================
// TEMPLATE CATEGORIES
// ============================================================================

export const TEMPLATE_CATEGORIES = {
  mint: {
    id: 'mint',
    name: 'MINT',
    icon: '🔬',
    description: 'Mathematik, Informatik, Naturwissenschaften, Technik'
  },
  languages: {
    id: 'languages',
    name: 'Sprachen',
    icon: '📚',
    description: 'Deutsch, Englisch, Fremdsprachen'
  },
  humanities: {
    id: 'humanities',
    name: 'Geisteswissenschaften',
    icon: '🏛️',
    description: 'Geschichte, Politik, Ethik, Philosophie'
  },
  arts: {
    id: 'arts',
    name: 'Kunst & Musik',
    icon: '🎨',
    description: 'Bildende Kunst, Musik, Medien'
  }
};

// ============================================================================
// BASE JSON SCHEMA (shared across all templates)
// ============================================================================

const BASE_JSON_SCHEMA = `JSON Schema:
[
  {
    "question": "string - Der Text der Quiz-Frage",
    "codeBlock": {
      "language": "string - Sprache/Format (python, latex, text, etc.)",
      "code": "string - Code/Formel/Zitat (kann leer sein)"
    },
    "terminalOutput": "string - erwartete Ausgabe (optional)",
    "options": ["string array - 2-6 Antwortmöglichkeiten"],
    "correctAnswerIndices": "[number array] - Array mit Index/Indizes der korrekten Antwort(en). z.B. [0], [1], [2], [3] oder [0,2] bei Multiple Choice",
    "explanation": "string - Erklärung der Lösung (optional)"
  }
]

WICHTIG für correctAnswerIndices:
- Bei Single Choice: Array mit einem Element, z.B. [0], [1], [2], oder [3]
- Bei Multiple Choice: Array mit mehreren Elementen, z.B. [0, 2]
- VERTEILE die korrekten Antworten gleichmäßig über alle Positionen!
- Setze NICHT immer [0] als korrekte Antwort!`;

// ============================================================================
// TEMPLATE DEFINITIONS
// ============================================================================

export const PROMPT_TEMPLATES = {
  // ---------------------------------------------------------------------------
  // MINT - PROGRAMMIERUNG
  // ---------------------------------------------------------------------------
  coding: {
    id: 'coding',
    name: 'Programmierung',
    icon: '💻',
    category: 'mint',
    description: 'Quiz für Code, Algorithmen, Syntax und Programmierkonzepte',
    systemPrompt: `Du bist ein Experte für die Erstellung von Programmier-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

${BASE_JSON_SCHEMA}

BEISPIEL - Python mit Code und Output:
{
  "question": "Was gibt dieser Python-Code aus?",
  "codeBlock": {
    "language": "python",
    "code": "numbers = [1, 2, 3, 4, 5]\\nresult = sum(numbers)\\nprint(result)"
  },
  "terminalOutput": "15",
  "options": ["12345", "15", "[1, 2, 3, 4, 5]", "Error"],
  "correctAnswerIndices": [1],
  "explanation": "sum() addiert alle Elemente: 1+2+3+4+5 = 15"
}

BEISPIEL - Konzeptfrage ohne Code:
{
  "question": "Was ist der Hauptvorteil von Dependency Injection?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Schnellere Ausführungsgeschwindigkeit",
    "Geringerer Speicherverbrauch",
    "Bessere Testbarkeit und lose Kopplung",
    "Einfacherer Code"
  ],
  "correctAnswerIndices": [2]
}

Richtlinien:
- Variiere zwischen Code-Fragen und Konzeptfragen
- Verwende realistische Code-Snippets
- Distraktoren sollten typische Programmierfehler widerspiegeln
- Unterstütze: Python, JavaScript, Java, C#, SQL, Bash, etc.
- Füge explanation hinzu wenn hilfreich`,
    exampleTags: ['Python', 'JavaScript', 'Java', 'Algorithmen', 'Datenstrukturen', 'SQL', 'OOP', 'Web Development'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // MINT - MATHEMATIK
  // ---------------------------------------------------------------------------
  math: {
    id: 'math',
    name: 'Mathematik',
    icon: '�',
    category: 'mint',
    description: 'Formeln, Berechnungen, Konzepte mit LaTeX-Support',
    systemPrompt: `Du bist ein Experte für die Erstellung von Mathematik-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

LATEX NUTZUNG - INLINE:
- Formeln im Fließtext: $formel$ (z.B. "Berechne $x^2 + 2x + 1$")
- Wichtige Symbole: $\\frac{a}{b}$, $\\sqrt{x}$, $\\sum$, $\\int$, $\\pi$, $\\alpha$
- Griechische Buchstaben: $\\alpha$, $\\beta$, $\\gamma$, $\\theta$, $\\phi$
- Operatoren: $\\cdot$, $\\times$, $\\div$, $\\pm$, $\\leq$, $\\geq$, $\\neq$
- Mengen: $\\in$, $\\notin$, $\\subset$, $\\cup$, $\\cap$

LATEX NUTZUNG - CODE BLOCK (für größere Formeln):
Setze "language": "latex" im codeBlock für komplexe Formeln:
{
  "codeBlock": {
    "language": "latex",
    "code": "\\\\frac{-b \\\\pm \\\\sqrt{b^2 - 4ac}}{2a}"
  }
}

HINWEIS: Im JSON muss \\ doppelt escaped werden als \\\\

${BASE_JSON_SCHEMA}

BEISPIEL - Einfache Algebra (nur inline):
{
  "question": "Vereinfache den Term: $3x + 5x - 2x$",
  "codeBlock": { "language": "", "code": "" },
  "options": ["$8x$", "$6x$", "$10x$", "$6$"],
  "correctAnswerIndices": [1],
  "explanation": "Gleiche Variablen addieren: $3 + 5 - 2 = 6$, also $6x$"
}

BEISPIEL - Komplexe Formel (mit latex codeBlock):
{
  "question": "Wende die quadratische Lösungsformel auf $x^2 + 4x - 5 = 0$ an. Was sind die Lösungen?",
  "codeBlock": {
    "language": "latex",
    "code": "x_{1,2} = \\\\frac{-b \\\\pm \\\\sqrt{b^2 - 4ac}}{2a}"
  },
  "options": ["$x_1 = -1, x_2 = 5$", "$x_1 = 2, x_2 = -2$", "$x_1 = 1, x_2 = -5$", "$x_1 = 4, x_2 = -1$"],
  "correctAnswerIndices": [2],
  "explanation": "Mit $a=1, b=4, c=-5$: $x = \\\\frac{-4 \\\\pm \\\\sqrt{16+20}}{2} = \\\\frac{-4 \\\\pm 6}{2}$"
}

BEISPIEL - Integral (mit latex codeBlock):
{
  "question": "Berechne das bestimmte Integral:",
  "codeBlock": {
    "language": "latex",
    "code": "\\\\int_0^2 x^2 \\\\, dx"
  },
  "options": ["$4$", "$\\\\frac{4}{3}$", "$2$", "$\\\\frac{8}{3}$"],
  "correctAnswerIndices": [3],
  "explanation": "$\\\\left[\\\\frac{x^3}{3}\\\\right]_0^2 = \\\\frac{8}{3} - 0 = \\\\frac{8}{3}$"
}

BEISPIEL - Geometrie:
{
  "question": "Die Fläche eines Kreises mit Radius $r = 3$ cm ist:",
  "codeBlock": { "language": "", "code": "" },
  "options": ["$6\\\\pi$ cm²", "$9\\\\pi$ cm²", "$3\\\\pi$ cm²", "$27\\\\pi$ cm²"],
  "correctAnswerIndices": [1],
  "explanation": "$A = \\\\pi r^2 = \\\\pi \\\\cdot 9 = 9\\\\pi$ cm²"
}

Richtlinien:
- Verwende $..$ für inline-Formeln und codeBlock mit "language": "latex" für große Formeln
- Variiere zwischen Rechnen und Konzeptfragen
- Distraktoren = typische Rechenfehler
- Bereiche: Algebra, Analysis, Geometrie, Stochastik, Lineare Algebra`,
    exampleTags: ['Algebra', 'Analysis', 'Geometrie', 'Stochastik', 'Lineare Funktionen', 'Gleichungen', 'Trigonometrie', 'Integrale', 'Differentialrechnung'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // MINT - PHYSIK
  // ---------------------------------------------------------------------------
  physics: {
    id: 'physics',
    name: 'Physik',
    icon: '⚛️',
    category: 'mint',
    description: 'Mechanik, Elektrodynamik, Thermodynamik mit Formeln',
    systemPrompt: `Du bist ein Experte für die Erstellung von Physik-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

LATEX NUTZUNG - INLINE:
- Einheiten: $\\text{m/s}$, $\\text{kg}$, $\\text{N}$, $\\text{J}$, $\\Omega$
- Vektoren: $\\vec{F}$, $\\vec{v}$, $\\vec{a}$
- Indizes: $v_0$, $a_{max}$, $E_{kin}$, $E_{pot}$
- Formeln inline: $F = ma$, $E = mc^2$, $s = v \\cdot t$

LATEX NUTZUNG - CODE BLOCK (für größere Formeln):
Setze "language": "latex" im codeBlock für komplexe Formeln:
{
  "codeBlock": {
    "language": "latex",
    "code": "E_{kin} = \\\\frac{1}{2}mv^2"
  }
}

HINWEIS: Im JSON muss \\ doppelt escaped werden als \\\\

${BASE_JSON_SCHEMA}

BEISPIEL - Mechanik (inline):
{
  "question": "Ein Auto beschleunigt gleichmäßig von 0 auf $20 \\\\text{ m/s}$ in 5 Sekunden. Wie groß ist die Beschleunigung?",
  "codeBlock": { "language": "", "code": "" },
  "options": ["$100 \\\\text{ m/s}^2$", "$4 \\\\text{ m/s}^2$", "$25 \\\\text{ m/s}^2$", "$5 \\\\text{ m/s}^2$"],
  "correctAnswerIndices": [1],
  "explanation": "$a = \\\\frac{\\\\Delta v}{\\\\Delta t} = \\\\frac{20}{5} = 4 \\\\text{ m/s}^2$"
}

BEISPIEL - Energieerhaltung (mit latex codeBlock):
{
  "question": "Ein Ball mit Masse $m = 2\\\\text{ kg}$ wird aus $h = 5\\\\text{ m}$ Höhe fallengelassen. Welche Geschwindigkeit hat er am Boden?",
  "codeBlock": {
    "language": "latex",
    "code": "E_{pot} = E_{kin} \\\\Rightarrow mgh = \\\\frac{1}{2}mv^2 \\\\Rightarrow v = \\\\sqrt{2gh}"
  },
  "options": ["$\\\\approx 5 \\\\text{ m/s}$", "$\\\\approx 20 \\\\text{ m/s}$", "$\\\\approx 10 \\\\text{ m/s}$", "$\\\\approx 7 \\\\text{ m/s}$"],
  "correctAnswerIndices": [2],
  "explanation": "$v = \\\\sqrt{2 \\\\cdot 10 \\\\cdot 5} = \\\\sqrt{100} = 10 \\\\text{ m/s}$"
}

BEISPIEL - Elektrizität (mit latex codeBlock):
{
  "question": "Ein Stromkreis hat $U = 12\\\\text{ V}$ und $R = 4\\\\Omega$. Wie groß ist der Strom?",
  "codeBlock": {
    "language": "latex",
    "code": "I = \\\\frac{U}{R}"
  },
  "options": ["$48\\\\text{ A}$", "$0.33\\\\text{ A}$", "$16\\\\text{ A}$", "$3\\\\text{ A}$"],
  "correctAnswerIndices": [3],
  "explanation": "$I = \\\\frac{12}{4} = 3\\\\text{ A}$ (Ohmsches Gesetz)"
}

BEISPIEL - Konzeptfrage:
{
  "question": "Was passiert mit der kinetischen Energie eines Objekts, wenn seine Geschwindigkeit verdoppelt wird?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Sie verdoppelt sich",
    "Sie bleibt gleich",
    "Sie vervierfacht sich",
    "Sie halbiert sich"
  ],
  "correctAnswerIndices": [2],
  "explanation": "$E_{kin} = \\\\frac{1}{2}mv^2$ - quadratische Abhängigkeit von $v$"
}

Richtlinien:
- Verwende SI-Einheiten mit $\\text{...}$ und LaTeX für Formeln
- Nutze latex codeBlock für Herleitungen und komplexe Formeln
- Mische Berechnungen mit Konzeptverständnis
- Bereiche: Mechanik, Elektrizität, Wärmelehre, Optik, Atomphysik`,
    exampleTags: ['Mechanik', 'Elektrizität', 'Thermodynamik', 'Optik', 'Wellen', 'Atomphysik', 'Energie', 'Kinetik', 'Elektromagnetismus'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // MINT - CHEMIE
  // ---------------------------------------------------------------------------
  chemistry: {
    id: 'chemistry',
    name: 'Chemie',
    icon: '⚗️',
    category: 'mint',
    description: 'Reaktionen, Elemente, Bindungen, Stöchiometrie',
    systemPrompt: `Du bist ein Experte für die Erstellung von Chemie-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

CHEMISCHE NOTATION:
- Summenformeln als Text: H₂O, CO₂, NaCl (mit Subskript-Zeichen)
- Reaktionsgleichungen im codeBlock mit "language": "text" oder "latex"
- Ionenladungen mit LaTeX: $Na^+$, $Cl^-$, $SO_4^{2-}$
- Für komplexe Formeln nutze "language": "latex"

LATEX FÜR CHEMIE:
- Reaktionspfeile: $\\rightarrow$, $\\leftarrow$, $\\rightleftharpoons$
- Zustandssymbole: $_{(s)}$, $_{(l)}$, $_{(g)}$, $_{(aq)}$
- Delta/Wärme: $\\Delta H$, $\\Delta G$
- Konzentrationen: $[H^+]$, $[OH^-]$

${BASE_JSON_SCHEMA}

BEISPIEL - Reaktionsgleichung:
{
  "question": "Welches Produkt entsteht bei der Reaktion von Natrium mit Wasser?",
  "codeBlock": {
    "language": "latex",
    "code": "2Na_{(s)} + 2H_2O_{(l)} \\\\rightarrow 2NaOH_{(aq)} + H_2\\\\uparrow"
  },
  "options": ["NaCl (Natriumchlorid)", "NaOH (Natriumhydroxid)", "Na₂O (Natriumoxid)", "NaH (Natriumhydrid)"],
  "correctAnswerIndices": [1],
  "explanation": "Natrium reagiert heftig mit Wasser zu Natriumhydroxid und Wasserstoff"
}

BEISPIEL - Stöchiometrie:
{
  "question": "Berechne die molare Masse von $H_2SO_4$ (Schwefelsäure).",
  "codeBlock": {
    "language": "latex",
    "code": "M = 2 \\\\cdot M_H + M_S + 4 \\\\cdot M_O"
  },
  "options": ["$49\\\\text{ g/mol}$", "$64\\\\text{ g/mol}$", "$98\\\\text{ g/mol}$", "$80\\\\text{ g/mol}$"],
  "correctAnswerIndices": [2],
  "explanation": "$2 \\\\cdot 1 + 32 + 4 \\\\cdot 16 = 2 + 32 + 64 = 98\\\\text{ g/mol}$"
}

BEISPIEL - pH-Wert:
{
  "question": "Eine Lösung hat $[H^+] = 10^{-3}\\\\text{ mol/L}$. Wie hoch ist der pH-Wert?",
  "codeBlock": { "language": "", "code": "" },
  "options": ["$pH = 11$", "$pH = 7$", "$pH = -3$", "$pH = 3$"],
  "correctAnswerIndices": [3],
  "explanation": "$pH = -\\\\log_{10}[H^+] = -\\\\log_{10}(10^{-3}) = 3$"
}

BEISPIEL - Konzept:
{
  "question": "Welche Bindungsart liegt in einem Kochsalzkristall (NaCl) vor?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Kovalente Bindung",
    "Metallbindung",
    "Ionenbindung",
    "Van-der-Waals-Bindung"
  ],
  "correctAnswerIndices": [2],
  "explanation": "NaCl besteht aus $Na^+$ und $Cl^-$ Ionen - typische Ionenbindung"
}

Richtlinien:
- Verwende LaTeX für Formeln, Konzentrationen und Berechnungen
- Nutze codeBlock mit "latex" für Reaktionsgleichungen
- Mische Reaktionen, Konzepte und Berechnungen
- Bereiche: Allgemeine Chemie, Organik, Anorganik, Biochemie, Stöchiometrie`,
    exampleTags: ['Periodensystem', 'Reaktionen', 'Organische Chemie', 'Säuren & Basen', 'Redox', 'Bindungen', 'Stöchiometrie', 'pH-Wert'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // MINT - BIOLOGIE
  // ---------------------------------------------------------------------------
  biology: {
    id: 'biology',
    name: 'Biologie',
    icon: '🧬',
    category: 'mint',
    description: 'Zellbiologie, Genetik, Evolution, Ökologie',
    systemPrompt: `Du bist ein Experte für die Erstellung von Biologie-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

BIOLOGISCHE NOTATION:
- DNA/RNA-Sequenzen im codeBlock mit "language": "text"
- Chemische Formeln mit LaTeX: $C_6H_{12}O_6$, $CO_2$, $ATP$
- Reaktionsgleichungen: $6CO_2 + 6H_2O \\rightarrow C_6H_{12}O_6 + 6O_2$

${BASE_JSON_SCHEMA}

BEISPIEL - Zellbiologie:
{
  "question": "Welches Organell ist für die Zellatmung verantwortlich?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Ribosomen",
    "Endoplasmatisches Retikulum",
    "Mitochondrien",
    "Golgi-Apparat"
  ],
  "correctAnswerIndices": [2],
  "explanation": "Mitochondrien sind die 'Kraftwerke der Zelle' - hier findet die ATP-Produktion statt"
}

BEISPIEL - Genetik:
{
  "question": "Welche Basenpaarung ist in der DNA korrekt?",
  "codeBlock": {
    "language": "text",
    "code": "DNA-Strang: 5'-ATCG-3'\\nGegenstrang: 3'-????-5'"
  },
  "options": ["UAGC", "ATCG", "TAGC", "GCTA"],
  "correctAnswerIndices": [2],
  "explanation": "A paart mit T, C paart mit G (komplementäre Basenpaarung)"
}

BEISPIEL - Photosynthese (mit LaTeX):
{
  "question": "Welche Summengleichung beschreibt die Photosynthese korrekt?",
  "codeBlock": {
    "language": "latex",
    "code": "6CO_2 + 6H_2O \\\\xrightarrow{Licht} C_6H_{12}O_6 + 6O_2"
  },
  "options": [
    "Glucose wird zu Kohlendioxid und Wasser abgebaut",
    "Kohlendioxid und Wasser werden zu Glucose und Sauerstoff",
    "Sauerstoff und Glucose werden zu Wasser",
    "ATP wird direkt aus Sonnenlicht erzeugt"
  ],
  "correctAnswerIndices": [1],
  "explanation": "Die Photosynthese wandelt $CO_2$ und $H_2O$ mit Lichtenergie in Glucose ($C_6H_{12}O_6$) und $O_2$ um"
}

Richtlinien:
- Verwende korrekte biologische Fachbegriffe
- Nutze LaTeX für chemische Formeln und Reaktionen
- Mische Fakten mit Verständnisfragen
- Bereiche: Zellbiologie, Genetik, Evolution, Ökologie, Anatomie, Stoffwechsel`,
    exampleTags: ['Zellbiologie', 'Genetik', 'Evolution', 'Ökologie', 'Anatomie', 'Stoffwechsel', 'Nervensystem', 'Photosynthese'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // SPRACHEN - ENGLISCH
  // ---------------------------------------------------------------------------
  english: {
    id: 'english',
    name: 'Englisch',
    icon: '🇬🇧',
    category: 'languages',
    description: 'Grammar, Vocabulary, Reading Comprehension',
    systemPrompt: `You are an expert at creating English quiz questions for German-speaking learners.

CRITICAL: Respond ONLY with valid JSON. No markdown.

${BASE_JSON_SCHEMA}

EXAMPLE - Grammar:
{
  "question": "Choose the correct form: She ___ to school every day.",
  "codeBlock": { "language": "", "code": "" },
  "options": ["go", "going", "goes", "gone"],
  "correctAnswerIndices": [2],
  "explanation": "Third person singular (she/he/it) requires the -s ending: she goes"
}

EXAMPLE - Vocabulary:
{
  "question": "What does 'nevertheless' mean in German?",
  "codeBlock": { "language": "", "code": "" },
  "options": ["niemals", "deshalb", "außerdem", "trotzdem"],
  "correctAnswerIndices": [3],
  "explanation": "'Nevertheless' = 'trotzdem/dennoch' - expresses contrast"
}

EXAMPLE - Reading Comprehension:
{
  "question": "Based on the text, why did the character decide to leave?",
  "codeBlock": {
    "language": "text",
    "code": "'I can't stay here anymore,' she said, looking at the old house one last time. The memories were too painful."
  },
  "options": [
    "The house was too old",
    "She had a new job",
    "The memories were painful",
    "Her family moved away"
  ],
  "correctAnswerIndices": [2],
  "explanation": "The text explicitly states 'The memories were too painful'"
}

Guidelines:
- Mix grammar, vocabulary, and reading comprehension
- Distractors should reflect common German-speaker mistakes
- Include CEFR-appropriate vocabulary (A1-C1)
- Cover: Tenses, Prepositions, Phrasal Verbs, Idioms`,
    exampleTags: ['Grammar', 'Vocabulary', 'Tenses', 'Prepositions', 'Reading', 'Phrasal Verbs', 'Idioms'],
    defaultSettings: {
      includeCodeBlocks: false,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // SPRACHEN - DEUTSCH
  // ---------------------------------------------------------------------------
  german: {
    id: 'german',
    name: 'Deutsch',
    icon: '🇩🇪',
    category: 'languages',
    description: 'Grammatik, Rechtschreibung, Textanalyse, Literatur',
    systemPrompt: `Du bist ein Experte für die Erstellung von Deutsch-Quiz-Fragen (Deutsch als Schulfach).

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

${BASE_JSON_SCHEMA}

BEISPIEL - Grammatik:
{
  "question": "Welcher Satz ist grammatikalisch korrekt?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Wegen dem Regen blieben wir zu Hause.",
    "Wegen des Regens blieben wir zu Hause.",
    "Wegen den Regen blieben wir zu Hause.",
    "Wegen der Regen blieben wir zu Hause."
  ],
  "correctAnswerIndices": [1],
  "explanation": "'Wegen' verlangt den Genitiv: des Regens (nicht dem Regen)"
}

BEISPIEL - Textanalyse:
{
  "question": "Welches Stilmittel liegt in folgendem Satz vor?",
  "codeBlock": {
    "language": "text",
    "code": "Die Sonne lacht vom Himmel."
  },
  "options": ["Metapher", "Hyperbel", "Personifikation", "Alliteration"],
  "correctAnswerIndices": [2],
  "explanation": "Der Sonne wird eine menschliche Eigenschaft (lachen) zugeschrieben = Personifikation"
}

BEISPIEL - Rechtschreibung:
{
  "question": "Welches Wort ist korrekt geschrieben?",
  "codeBlock": { "language": "", "code": "" },
  "options": ["Rhytmus", "Rythmus", "Rhytmuss", "Rhythmus"],
  "correctAnswerIndices": [3],
  "explanation": "Korrekte Schreibung: Rhythmus (griechischer Ursprung)"
}

Richtlinien:
- Mische Grammatik, Rechtschreibung und Textanalyse
- Bei Literatur: Epochen, Stilmittel, Interpretation
- Verwende authentische Textstellen wenn möglich
- Bereiche: Grammatik, Rechtschreibung, Stilistik, Literatur`,
    exampleTags: ['Grammatik', 'Rechtschreibung', 'Stilmittel', 'Literatur', 'Textanalyse', 'Epochen', 'Zeichensetzung'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // SPRACHEN - FRANZÖSISCH
  // ---------------------------------------------------------------------------
  french: {
    id: 'french',
    name: 'Französisch',
    icon: '🇫🇷',
    category: 'languages',
    description: 'Grammaire, Vocabulaire, Compréhension',
    systemPrompt: `Tu es un expert pour créer des questions de quiz en français pour des apprenants germanophones.

CRITIQUE: Réponds UNIQUEMENT avec du JSON valide. Pas de markdown.

${BASE_JSON_SCHEMA}

EXEMPLE - Grammaire:
{
  "question": "Choisissez la forme correcte: Hier, nous ___ au cinéma.",
  "codeBlock": { "language": "", "code": "" },
  "options": ["avons allé", "allions", "sommes allés", "irons"],
  "correctAnswerIndices": [2],
  "explanation": "Passé composé avec 'être' pour les verbes de mouvement: nous sommes allés"
}

EXEMPLE - Vocabulaire:
{
  "question": "Que signifie 'pourtant' en allemand?",
  "codeBlock": { "language": "", "code": "" },
  "options": ["deshalb", "außerdem", "niemals", "trotzdem"],
  "correctAnswerIndices": [3],
  "explanation": "'Pourtant' = 'trotzdem/dennoch' - exprime une opposition"
}

Directives:
- Mélangez grammaire, vocabulaire et compréhension
- Les distracteurs doivent refléter les erreurs typiques des germanophones
- Niveaux: A1-B2`,
    exampleTags: ['Grammaire', 'Vocabulaire', 'Passé composé', 'Subjonctif', 'Articles', 'Pronoms'],
    defaultSettings: {
      includeCodeBlocks: false,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // GEISTESWISSENSCHAFTEN - GESCHICHTE
  // ---------------------------------------------------------------------------
  history: {
    id: 'history',
    name: 'Geschichte',
    icon: '🏛️',
    category: 'humanities',
    description: 'Epochen, Ereignisse, Quellenanalyse',
    systemPrompt: `Du bist ein Experte für die Erstellung von Geschichte-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

${BASE_JSON_SCHEMA}

BEISPIEL - Mit Quelle:
{
  "question": "Welche Aussage lässt sich aus dieser Quelle ableiten?",
  "codeBlock": {
    "language": "text",
    "code": "\\"Wir haben nicht Krieg geführt, um Völker zu unterdrücken oder fremde Nationen zu unterjochen.\\"\\n- Woodrow Wilson, 1918"
  },
  "options": [
    "Wilson rechtfertigt territoriale Expansion",
    "Wilson fordert Reparationen",
    "Wilson betont idealistische Kriegsziele",
    "Wilson kritisiert die Alliierten"
  ],
  "correctAnswerIndices": [2],
  "explanation": "Wilson betont, dass die USA nicht aus imperialistischen Motiven kämpften - Teil seiner '14 Punkte'"
}

BEISPIEL - Konzept:
{
  "question": "Welches Ereignis markiert den Beginn des Ersten Weltkriegs?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Lusitania-Versenkung (1915)",
    "Attentat von Sarajevo (1914)",
    "Russische Revolution (1917)",
    "Versailler Vertrag (1919)"
  ],
  "correctAnswerIndices": [1],
  "explanation": "Das Attentat auf Franz Ferdinand am 28. Juni 1914 löste die Julikrise aus"
}

BEISPIEL - Analyse:
{
  "question": "Welche langfristige Ursache trug zum Ausbruch des Ersten Weltkriegs bei?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Die Ermordung Franz Ferdinands",
    "Der Versailler Vertrag",
    "Imperialistische Rivalitäten",
    "Die Weltwirtschaftskrise"
  ],
  "correctAnswerIndices": [2],
  "explanation": "Imperialismus war eine langfristige Ursache; das Attentat war der Auslöser (kurzfristig)"
}

Richtlinien:
- Fokus auf Ursachen, Wirkungen und Zusammenhänge
- Vermeide reine Jahreszahlen-Abfragen
- Nutze Quellen für Analysefragen
- Beziehe verschiedene Perspektiven ein
- Bereiche: Antike, Mittelalter, Neuzeit, 20. Jahrhundert`,
    exampleTags: ['Antike', 'Mittelalter', 'Neuzeit', 'Weltkriege', 'Kalter Krieg', 'Deutsche Geschichte', 'Quellenanalyse'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // GEISTESWISSENSCHAFTEN - POLITIK/SOZIALKUNDE
  // ---------------------------------------------------------------------------
  politics: {
    id: 'politics',
    name: 'Politik & Gesellschaft',
    icon: '🏢',
    category: 'humanities',
    description: 'Politische Systeme, Demokratie, Gesellschaft',
    systemPrompt: `Du bist ein Experte für die Erstellung von Politik/Sozialkunde-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

${BASE_JSON_SCHEMA}

BEISPIEL - Institutionen:
{
  "question": "Welches Organ wählt den deutschen Bundeskanzler?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Der Bundesrat",
    "Das Volk direkt",
    "Der Bundespräsident allein",
    "Der Bundestag"
  ],
  "correctAnswerIndices": [3],
  "explanation": "Der Bundeskanzler wird vom Bundestag auf Vorschlag des Bundespräsidenten gewählt (Art. 63 GG)"
}

BEISPIEL - Konzept:
{
  "question": "Was ist ein wesentliches Merkmal einer repräsentativen Demokratie?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Das Volk stimmt über alle Gesetze direkt ab",
    "Ein Monarch hat die Entscheidungsgewalt",
    "Gewählte Vertreter entscheiden für das Volk",
    "Es gibt keine Gewaltenteilung"
  ],
  "correctAnswerIndices": [2],
  "explanation": "In einer repräsentativen Demokratie wählt das Volk Vertreter, die in seinem Namen entscheiden"
}

Richtlinien:
- Fokus auf Verständnis, nicht nur Fakten
- Beziehe aktuelle politische Themen ein
- Bereiche: Demokratie, Grundgesetz, EU, Internationale Politik, Wirtschaftspolitik`,
    exampleTags: ['Demokratie', 'Grundgesetz', 'Parteien', 'Wahlen', 'EU', 'Internationale Politik', 'Grundrechte'],
    defaultSettings: {
      includeCodeBlocks: false,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // GEISTESWISSENSCHAFTEN - PHILOSOPHIE/ETHIK
  // ---------------------------------------------------------------------------
  philosophy: {
    id: 'philosophy',
    name: 'Philosophie & Ethik',
    icon: '🤔',
    category: 'humanities',
    description: 'Ethische Theorien, Philosophen, moralische Dilemmata',
    systemPrompt: `Du bist ein Experte für die Erstellung von Philosophie/Ethik-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

${BASE_JSON_SCHEMA}

BEISPIEL - Ethische Theorie:
{
  "question": "Welche ethische Position vertritt Immanuel Kant mit dem kategorischen Imperativ?",
  "codeBlock": {
    "language": "text",
    "code": "\\"Handle nur nach derjenigen Maxime, durch die du zugleich wollen kannst, dass sie ein allgemeines Gesetz werde.\\""
  },
  "options": [
    "Utilitarismus",
    "Tugendethik",
    "Deontologische Ethik",
    "Egoismus"
  ],
  "correctAnswerIndices": [2],
  "explanation": "Kant vertritt eine Pflichtethik (Deontologie): Die Handlung selbst muss moralisch sein, nicht nur ihre Folgen"
}

BEISPIEL - Philosophen:
{
  "question": "Welcher Philosoph prägte den Begriff 'Existenzialismus'?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Friedrich Nietzsche",
    "Platon",
    "John Locke",
    "Jean-Paul Sartre"
  ],
  "correctAnswerIndices": [3],
  "explanation": "Sartre: 'Der Mensch ist zur Freiheit verurteilt' - Grundgedanke des Existenzialismus"
}

Richtlinien:
- Verwende Originalzitate wenn möglich
- Mische Theorie mit Anwendungsbeispielen
- Bereiche: Ethik, Erkenntnistheorie, Philosophiegeschichte, angewandte Ethik`,
    exampleTags: ['Ethik', 'Kant', 'Utilitarismus', 'Existenzialismus', 'Erkenntnistheorie', 'Moralphilosophie'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // KUNST & MUSIK
  // ---------------------------------------------------------------------------
  art: {
    id: 'art',
    name: 'Kunst',
    icon: '🎨',
    category: 'arts',
    description: 'Kunstgeschichte, Stile, Künstler, Techniken',
    systemPrompt: `Du bist ein Experte für die Erstellung von Kunst-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

${BASE_JSON_SCHEMA}

BEISPIEL - Kunstepochen:
{
  "question": "Welche Kunstepoche ist durch starke Kontraste von Licht und Schatten (Chiaroscuro) gekennzeichnet?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Impressionismus",
    "Renaissance",
    "Barock",
    "Expressionismus"
  ],
  "correctAnswerIndices": [2],
  "explanation": "Barockkünstler wie Caravaggio nutzten dramatische Hell-Dunkel-Kontraste"
}

BEISPIEL - Künstler:
{
  "question": "Welcher Künstler schuf das Werk 'Die Sternennacht'?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Claude Monet",
    "Pablo Picasso",
    "Salvador Dalí",
    "Vincent van Gogh"
  ],
  "correctAnswerIndices": [3],
  "explanation": "Van Gogh malte 'Die Sternennacht' 1889 während seines Aufenthalts in einer psychiatrischen Anstalt"
}

Richtlinien:
- Mische Epochen, Künstler und Techniken
- Bei Werkanalysen: Beschreibe das Werk im codeBlock
- Bereiche: Kunstgeschichte, Malerei, Skulptur, Architektur, Fotografie`,
    exampleTags: ['Renaissance', 'Barock', 'Impressionismus', 'Moderne Kunst', 'Architektur', 'Fotografie'],
    defaultSettings: {
      includeCodeBlocks: false,
      difficulty: 'medium'
    }
  },

  music: {
    id: 'music',
    name: 'Musik',
    icon: '🎵',
    category: 'arts',
    description: 'Musiktheorie, Komponisten, Epochen, Instrumente',
    systemPrompt: `Du bist ein Experte für die Erstellung von Musik-Quiz-Fragen auf Deutsch.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

MUSIKTHEORIE NOTATION:
- Intervalle und Frequenzen mit LaTeX: $f = 440\\text{ Hz}$
- Verhältnisse: $\\frac{3}{2}$ (Quinte), $\\frac{4}{3}$ (Quarte)
- Dynamikbezeichnungen als Text: pp, p, mp, mf, f, ff
- Notennamen im codeBlock bei Bedarf

${BASE_JSON_SCHEMA}

BEISPIEL - Musiktheorie (mit LaTeX):
{
  "question": "Wie viele Halbtöne umfasst eine große Terz?",
  "codeBlock": { "language": "", "code": "" },
  "options": ["3 Halbtöne", "5 Halbtöne", "2 Halbtöne", "4 Halbtöne"],
  "correctAnswerIndices": [3],
  "explanation": "Große Terz = 4 Halbtöne (z.B. C-E), kleine Terz = 3 Halbtöne"
}

BEISPIEL - Akustik (mit LaTeX):
{
  "question": "Der Kammerton a' hat eine Frequenz von $440\\\\text{ Hz}$. Welche Frequenz hat das a'' (eine Oktave höher)?",
  "codeBlock": {
    "language": "latex",
    "code": "f_{Oktave} = 2 \\\\cdot f_{Grundton}"
  },
  "options": ["$660\\\\text{ Hz}$", "$880\\\\text{ Hz}$", "$220\\\\text{ Hz}$", "$440\\\\text{ Hz}$"],
  "correctAnswerIndices": [1],
  "explanation": "Eine Oktave entspricht einer Frequenzverdopplung: $440 \\\\cdot 2 = 880\\\\text{ Hz}$"
}

BEISPIEL - Komponisten:
{
  "question": "Welcher Komponist schrieb die 'Mondscheinsonate'?",
  "codeBlock": { "language": "", "code": "" },
  "options": [
    "Wolfgang Amadeus Mozart",
    "Johann Sebastian Bach",
    "Ludwig van Beethoven",
    "Frédéric Chopin"
  ],
  "correctAnswerIndices": [2],
  "explanation": "Beethovens Klaviersonate Nr. 14 (1801), der Name 'Mondscheinsonate' stammt von einem Kritiker"
}

Richtlinien:
- Mische Theorie, Geschichte und Akustik
- Verwende LaTeX für Frequenzen und mathematische Verhältnisse
- Bereiche: Musiktheorie, Komponisten, Epochen, Instrumente, Gattungen, Akustik`,
    exampleTags: ['Musiktheorie', 'Klassik', 'Romantik', 'Jazz', 'Instrumente', 'Komponisten', 'Notenlehre', 'Akustik'],
    defaultSettings: {
      includeCodeBlocks: true,
      difficulty: 'medium'
    }
  },

  // ---------------------------------------------------------------------------
  // ALLGEMEIN / CUSTOM
  // ---------------------------------------------------------------------------
  general: {
    id: 'general',
    name: 'Allgemein',
    icon: '📋',
    category: 'mint',
    description: 'Flexibles Template für beliebige Themen',
    systemPrompt: `Du bist ein Experte für die Erstellung von Quiz-Fragen auf Deutsch zu beliebigen Themen.

KRITISCH: Antworte NUR mit gültigem JSON. Keine Markdown-Formatierung.

VERFÜGBARE FORMATIERUNGEN:
- Inline-LaTeX für Formeln: $formel$ (z.B. $x^2$, $\\frac{a}{b}$)
- Code-Block für größere Inhalte:
  - "language": "latex" für mathematische Formeln
  - "language": "python", "javascript", etc. für Code
  - "language": "text" für allgemeinen formatierten Text

${BASE_JSON_SCHEMA}

BEISPIEL - Textbasiert:
{
  "question": "Beispielfrage zum angegebenen Thema?",
  "codeBlock": { "language": "", "code": "" },
  "options": ["Falsche Option A", "Falsche Option B", "Richtige Antwort", "Falsche Option C"],
  "correctAnswerIndices": [2],
  "explanation": "Erklärung warum diese Antwort korrekt ist"
}

BEISPIEL - Mit Formel:
{
  "question": "Berechne das Ergebnis:",
  "codeBlock": {
    "language": "latex",
    "code": "\\\\frac{a^2 + b^2}{c}"
  },
  "options": ["$\\\\frac{16}{4} = 4$", "$\\\\frac{9}{3} = 3$", "$\\\\frac{36}{6} = 6$", "$\\\\frac{25}{5} = 5$"],
  "correctAnswerIndices": [3],
  "explanation": "Mit $a=3, b=4, c=5$: $\\\\frac{9+16}{5} = 5$"
}

Richtlinien:
- Erstelle Fragen, die Verständnis prüfen
- Variiere Schwierigkeitsgrad
- Mache falsche Antworten (Distraktoren) plausibel
- Halte Fragen klar und prägnant
- Verwende das passende Format (Text, Code, Formeln) je nach Thema
- Nutze LaTeX für mathematische Ausdrücke`,
    exampleTags: [],
    defaultSettings: {
      includeCodeBlocks: false,
      difficulty: 'medium'
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get a template by its ID
 */
export function getTemplateById(templateId) {
  return PROMPT_TEMPLATES[templateId] || null;
}

/**
 * Get all templates for a specific category
 */
export function getTemplatesByCategory(categoryId) {
  return Object.values(PROMPT_TEMPLATES).filter(
    template => template.category === categoryId
  );
}

/**
 * Get all template IDs
 */
export function getAllTemplateIds() {
  return Object.keys(PROMPT_TEMPLATES);
}

/**
 * Get all categories with their templates
 */
export function getCategoriesWithTemplates() {
  return Object.values(TEMPLATE_CATEGORIES).map(category => ({
    ...category,
    templates: getTemplatesByCategory(category.id)
  }));
}

/**
 * Get the system prompt for a template (with optional custom overrides)
 */
export function getTemplateSystemPrompt(templateId, customOverrides = {}) {
  const template = getTemplateById(templateId);
  if (!template) return null;
  
  let prompt = template.systemPrompt;
  
  // Replace placeholders with custom values if provided
  Object.entries(customOverrides).forEach(([key, value]) => {
    prompt = prompt.replace(new RegExp(`{{${key}}}`, 'g'), value);
  });
  
  return prompt;
}

/**
 * Get default settings for a template
 */
export function getTemplateDefaultSettings(templateId) {
  const template = getTemplateById(templateId);
  return template?.defaultSettings || {
    includeCodeBlocks: false,
    difficulty: 'medium'
  };
}

/**
 * Get example tags for a template
 */
export function getTemplateExampleTags(templateId) {
  const template = getTemplateById(templateId);
  return template?.exampleTags || [];
}

// ============================================================================
// CUSTOM TEMPLATES (User-defined, stored in localStorage)
// ============================================================================

const CUSTOM_TEMPLATES_KEY = 'llm_custom_templates';

// Import storage service for centralized localStorage management
import { templateStorage } from '../../services/storageService';

/**
 * Get all custom templates from localStorage
 */
export function getCustomTemplates() {
  try {
    return templateStorage.get('custom_templates', {});
  } catch {
    return {};
  }
}

/**
 * Save a custom template to localStorage
 * @param {Object} template - Template object with id, name, icon, systemPrompt, etc.
 * @returns {boolean} - Success status
 */
export function saveCustomTemplate(template) {
  try {
    const templates = getCustomTemplates();
    const id = template.id || `custom_${Date.now()}`;
    
    templates[id] = {
      ...template,
      id,
      category: 'custom',
      isCustom: true,
      createdAt: template.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    templateStorage.set('custom_templates', templates);
    return { success: true, id };
  } catch (error) {
    log.error('Error saving custom template:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete a custom template from localStorage
 * @param {string} templateId - ID of the template to delete
 * @returns {boolean} - Success status
 */
export function deleteCustomTemplate(templateId) {
  try {
    const templates = getCustomTemplates();
    if (templates[templateId]) {
      delete templates[templateId];
      templateStorage.set('custom_templates', templates);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Get a template by ID (includes both built-in and custom templates)
 * @param {string} templateId
 */
export function getTemplateByIdWithCustom(templateId) {
  // First check built-in templates
  const builtIn = PROMPT_TEMPLATES[templateId];
  if (builtIn) return { ...builtIn, isCustom: false };
  
  // Then check custom templates
  const customTemplates = getCustomTemplates();
  if (customTemplates[templateId]) {
    return { ...customTemplates[templateId], isCustom: true };
  }
  
  return null;
}

/**
 * Get all categories with their templates (including custom)
 */
export function getCategoriesWithTemplatesIncludingCustom() {
  const categories = getCategoriesWithTemplates();
  const customTemplates = getCustomTemplates();
  const customList = Object.values(customTemplates);
  
  if (customList.length > 0) {
    categories.push({
      id: 'custom',
      name: 'Eigene Vorlagen',
      icon: '⚙️',
      description: 'Selbst erstellte Templates',
      templates: customList
    });
  }
  
  return categories;
}

/**
 * Export all custom templates as JSON for backup
 */
export function exportCustomTemplates() {
  return JSON.stringify(getCustomTemplates(), null, 2);
}

/**
 * Import custom templates from JSON
 * @param {string} jsonString - JSON string of templates
 * @param {boolean} overwrite - Whether to overwrite existing templates
 */
export function importCustomTemplates(jsonString, overwrite = false) {
  try {
    const imported = JSON.parse(jsonString);
    const existing = overwrite ? {} : getCustomTemplates();
    
    const merged = { ...existing, ...imported };
    templateStorage.set('custom_templates', merged);
    return { success: true, count: Object.keys(imported).length };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
